// Decides which language the viewer speaks, and remembers an explicit choice.
//
// Order (first match wins):
//   1. ?lang=en                 — what the portal appends when it embeds us, and
//                                 what a shared link carries
//   2. localStorage             — the in-app switcher; an explicit human choice
//                                 outranks the browser's guess but not an
//                                 explicit link
//   3. navigator.language(s)    — pt* → pt-BR, everything else → en
//   4. DEFAULT_LOCALE           — pt-BR
//
// Note on durability: urlStateSync rewrites only location.hash and preserves
// location.search (urlStateSync.ts:243), so ?lang= survives deep-link updates
// without needing a sessionStorage mirror.
//
// Embed mode adds a step the caller drives: the parent may send `locale` inside
// imersivo:tour:config-set, which arrives after boot and calls setStoredLocale +
// applyDocumentLocale directly.

import { DEFAULT_LOCALE, isLocale, LOCALE_STORAGE_KEY, type Locale } from './index.ts';

export function resolveLocale(search: string = window.location.search): Locale {
  const fromUrl = new URLSearchParams(search).get('lang');
  if (isLocale(fromUrl)) return fromUrl;
  // Tolerate `?lang=pt`, `?lang=pt_BR`, `?lang=en-GB` — a hand-typed link
  // should not silently fall back to the default.
  const normalised = normaliseTag(fromUrl);
  if (normalised) return normalised;

  const stored = readStoredLocale();
  if (stored) return stored;

  return fromNavigator() ?? DEFAULT_LOCALE;
}

/** `pt`, `pt_BR`, `pt-PT`, `en-GB` → a supported Locale, or null. */
export function normaliseTag(tag: string | null | undefined): Locale | null {
  if (!tag) return null;
  const base = tag.replace('_', '-').toLowerCase().split('-')[0];
  if (base === 'pt') return 'pt-BR';
  if (base === 'en') return 'en';
  return null;
}

function fromNavigator(): Locale | null {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const hit = normaliseTag(tag);
    if (hit) return hit;
  }
  return null;
}

export function readStoredLocale(): Locale | null {
  try {
    const v = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(v) ? v : null;
  } catch {
    // Safari private mode throws on localStorage access.
    return null;
  }
}

export function setStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* non-fatal — the choice just won't survive a reload */
  }
}

/** Keeps <html lang> honest so screen readers and TTS pick the right voice. */
export function applyDocumentLocale(locale: Locale): void {
  document.documentElement.lang = locale;
}
